import { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Controller, useWatch } from 'react-hook-form';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import { ErrorMessage } from '@hookform/error-message';

const DebouncedTextField = ({
    name,
    label,
    placeholder,
    helperText,
    control,
    errors,
    success,
    required,
    disabled,
    readOnly,
    inputProps,
    children,
    hiddenLabel,
    showError,
    showRequired,
    validationRules,
    ...otherProps
}) => {
    const [localValue, setLocalValue] = useState('');
    const isUserInput = useRef(false);
    const debounceTimeout = useRef(null);

    // Watch the field value for external changes
    const fieldValue = useWatch({ control, name });

    const isInvalid = !!errors[name];

    let describedBy = helperText ? `${name}-helper` : null;
    if (showError && isInvalid) {
        describedBy = describedBy ? `${describedBy} ${name}-error` : `${name}-error`;
    }

    const elementProps = {
        ...inputProps,
        id: name,
        'aria-label': hiddenLabel ? label : null,
        'aria-describedby': describedBy,
        'aria-invalid': isInvalid,
        'aria-required': required,
        'aria-disabled': disabled,
    };

    const debouncedUpdate = useCallback((onChange, value) => {
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(() => {
            onChange(value);
        }, 300);
    }, []);

    // Sync external changes to local state (when not from user input)
    useEffect(() => {
        if (!isUserInput.current && fieldValue !== localValue) {
            setLocalValue(fieldValue || '');
        }
        isUserInput.current = false;
    }, [fieldValue, localValue]);

    // Cleanup timeout on unmount
    useEffect(() => {
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }
    }, []);

    return (
        <Controller
            name={name}
            control={control}
            rules={validationRules}
            render={({ field }) => {
                const handleChange = (event) => {
                    const newValue = event.target.value;
                    isUserInput.current = true;
                    setLocalValue(newValue);
                    debouncedUpdate(field.onChange, newValue);
                };

                const handleBlur = (event) => {
                    field.onBlur();
                    if (otherProps.onBlur) {
                        otherProps.onBlur(event);
                    }
                };

                return (
                    <FormControl
                        variant="outlined"
                        component="fieldset"
                        error={isInvalid}
                        fullWidth
                        required={required}
                    >
                        {!hiddenLabel && (
                            <InputLabel
                                htmlFor={name}
                                required={showRequired && required}
                                disabled={disabled}
                                shrink={false}
                            >
                                {label}
                            </InputLabel>
                        )}

                        <OutlinedInput
                            inputRef={field.ref}
                            id={name}
                            value={localValue}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder={hiddenLabel ? placeholder : null}
                            required={required}
                            disabled={disabled}
                            readOnly={readOnly}
                            error={isInvalid}
                            notched={hiddenLabel && !placeholder ? false : undefined}
                            inputProps={elementProps}
                            {...otherProps}
                        />

                        {children}

                        {helperText && <FormHelperText id={`${name}-helper`}>{helperText}</FormHelperText>}

                        {showError && (
                            <FormHelperText id={`${name}-error`} role="alert" aria-live="polite" error>
                                <ErrorMessage name={name} errors={errors} />
                            </FormHelperText>
                        )}
                    </FormControl>
                );
            }}
        />
    );
};

DebouncedTextField.defaultProps = {
    placeholder: null,
    helperText: null,
    errors: {},
    success: false,
    disabled: false,
    required: false,
    readOnly: false,
    inputProps: {},
    children: null,
    hiddenLabel: false,
    showError: true,
    showRequired: false,
    validationRules: {},
};

DebouncedTextField.propTypes = {
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    placeholder: PropTypes.string,
    helperText: PropTypes.string,
    control: PropTypes.object.isRequired,
    errors: PropTypes.object,
    success: PropTypes.bool,
    required: PropTypes.bool,
    disabled: PropTypes.bool,
    readOnly: PropTypes.bool,
    inputProps: PropTypes.object,
    children: PropTypes.node,
    hiddenLabel: PropTypes.bool,
    showError: PropTypes.bool,
    showRequired: PropTypes.bool,
    validationRules: PropTypes.object,
};

export default DebouncedTextField;
